import { useEffect, useRef, useState } from 'react';

/*
 * One shared, throttled scroll loop for the whole site.
 *
 * Before: App kept `scrollPosition` in React state, so every scroll event
 * re-rendered the entire page, and sections compared it against hardcoded
 * pixel positions (e.g. `scrollPosition >= 12244`). Those numbers only
 * matched one screen size.
 *
 * Now: a single passive scroll/resize listener runs subscribers at most once
 * per animation frame. Sections ask "has my top edge reached this line on
 * the screen?", where the line is a fraction of the viewport height
 * (0 = top of screen, 1 = bottom). That works at any screen size or scale.
 *
 * The line values used in the components were derived from the old pixel
 * thresholds at the original 1366x768 design size:
 *   line = (sectionTop - oldThreshold) / 768
 * so desktop timing is unchanged.
 */

const subscribers = new Set();
let frame = null;

const run = () => {
  frame = null;
  const y = window.scrollY;
  const vh = window.innerHeight;
  subscribers.forEach((fn) => fn(y, vh));
};

export const requestScrollUpdate = () => {
  if (frame == null) frame = requestAnimationFrame(run);
};

export const subscribeScroll = (fn) => {
  if (subscribers.size === 0) {
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate);
    window.addEventListener('orientationchange', requestScrollUpdate);
  }
  subscribers.add(fn);
  requestScrollUpdate();
  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0) {
      window.removeEventListener('scroll', requestScrollUpdate);
      window.removeEventListener('resize', requestScrollUpdate);
      window.removeEventListener('orientationchange', requestScrollUpdate);
    }
  };
};

// Has the section been scrolled into place?
// True once its top edge is at or above `line`, or once the whole section is
// on screen (so tall screens don't have to scroll further than they can).
const isInto = (rect, vh, line) => rect.top <= line * vh || rect.bottom <= vh;

/** True once the element's top edge reaches `line` (fraction of viewport height). */
export const useScrolledInto = (ref, line) => {
  const [on, setOn] = useState(false);
  useEffect(
    () =>
      subscribeScroll((y, vh) => {
        const el = ref.current;
        if (el) setOn(isInto(el.getBoundingClientRect(), vh, line));
      }),
    [ref, line]
  );
  return on;
};

/**
 * True while the element's top edge is between `startLine` and `endLine`.
 * `endLine` can be negative (section partly scrolled off the top).
 */
export const useScrollRange = (ref, startLine, endLine) => {
  const [on, setOn] = useState(false);
  useEffect(
    () =>
      subscribeScroll((y, vh) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setOn(rect.top > endLine * vh && isInto(rect, vh, startLine));
      }),
    [ref, startLine, endLine]
  );
  return on;
};

/** Calls `fn(rect, viewportHeight)` once per frame while scrolling. For direct style updates (parallax). */
export const useScrollFrame = (ref, fn) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(
    () =>
      subscribeScroll((y, vh) => {
        const el = ref.current;
        if (el) fnRef.current(el.getBoundingClientRect(), vh);
      }),
    [ref]
  );
};
